/**
 * InstrumentIcon Component - Renders instrument icons with theme-aware styling
 */

import { memo } from 'react';
import type { InstrumentCategory } from '../../types/instruments';

/**
 * Props for InstrumentIcon component
 */
interface InstrumentIconProps {
  /** Icon emoji or identifier */
  icon: string;
  /** Instrument category for color coding */
  category: InstrumentCategory;
  /** Size variant */
  size?: 'sm' | 'md' | 'lg';
  /** Additional CSS classes */
  className?: string;
}

/**
 * Get category-specific color class
 */
function getCategoryColorClass(category: InstrumentCategory): string {
  const colorMap: Record<InstrumentCategory, string> = {
    Lead: 'instrument-icon-lead',
    Bass: 'instrument-icon-bass',
    Pad: 'instrument-icon-pad',
    Arp: 'instrument-icon-arp',
    Percussion: 'instrument-icon-percussion',
    SFX: 'instrument-icon-sfx',
  };
  return colorMap[category] || '';
}

/**
 * Get size class
 */
function getSizeClass(size: 'sm' | 'md' | 'lg'): string {
  const sizeMap = {
    sm: 'instrument-icon-sm',
    md: 'instrument-icon-md',
    lg: 'instrument-icon-lg',
  };
  return sizeMap[size];
}

/**
 * InstrumentIcon component
 * Renders an emoji icon with proper sizing and theme-aware styling
 * Optimized with React.memo to prevent unnecessary re-renders
 */
export const InstrumentIcon = memo(function InstrumentIcon({
  icon,
  category,
  size = 'md',
  className = '',
}: InstrumentIconProps) {
  const categoryClass = getCategoryColorClass(category);
  const sizeClass = getSizeClass(size);

  return (
    <span
      className={`instrument-icon ${categoryClass} ${sizeClass} ${className}`}
      role="img"
      aria-label={`${category} instrument icon`}
    >
      {icon}
    </span>
  );
});
